import {
  Alert,
  Card,
  Col,
  Container,
  Form,
  Header,
  LoadingScreen,
  Row,
  SectionHeader,
  Submit,
} from "@administrate/piston-ux";
import {
  FormConsumer,
  useTypedFormValues,
} from "@administrate/piston-ux/lib/Form";
import { SaveStates } from "@administrate/piston-ux/lib/types/detailTypes";
import { ensureObservableValue } from "@administrate/piston-ux/lib/withFormWrapper";
import { Observer, observer } from "mobx-react-lite";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useWebLinkClient,
  useWebLinkMutation,
  useWebLinkQuery,
} from "../../hooks/weblink";
import {
  POINT_OF_SALE_LEARNER_FIELD_QUERY,
  REGISTER_LEARNERS_ONTO_PRIVATE_BOOKING_MUTATION,
} from "../../queries/cart";

import { getContactVariables } from "../../components/ContactPicker";
import { ItemInformation } from "../../components/ItemInformation";
import {
  EventField,
  FilterOperation,
  Mutation,
  PointOfSaleField,
  PointOfSaleFieldEntityType,
  Query,
} from "../../generated/weblinkTypes";
import { useDateFormatter } from "../../hooks/useDateFormatter";
import { GET_MANAGED_CONTACTS_FOR_EVENT } from "../../queries/learnerManagement";
import { useHistory, useParams } from "../../useHistory";
import { hasErrors } from "../../utils/bookingHelpers";
import { mapPointOfSaleFieldValues } from "../../utils/customFieldValues";
import { formatDateRange } from "../../utils/dateTimeHelpers";
import { extractNodes } from "../../utils/extractNodes";
import { DEFAULT_EVENT } from "./ExistingEventBooking";

import {
  LearnerType,
  LearnersFormValues,
  MultipleLearnerSelector,
} from "../../components/LearnerSelection/MultipleLearnerSelector";
import { SelectedContact } from "../../components/LearnerSelection/selectedContact";
import { useAlreadyRegisteredEventIds } from "../../hooks/useAlreadyRegisteredEventIds";
import { useBookerIntention } from "../../hooks/useBookerIntention";
import { useViewer } from "../../providers/ViewerProvider";
import {
  PRIVATE_TRAINING_COORDINATOR_QUERY,
  PRIVATE_TRAINING_SELF_QUERY,
} from "../../queries/privateTraining";
import { BookerIntention } from "../Order/BookingPage";

const UNNAMED_LEARNER_CONTACT = {
  id: "unnamed",
  firstName: "Unnamed",
  lastName: "Learner",
};

const getInitialContact = (contact: SelectedContact): LearnerType => ({
  learnerNumber: 0,
  posFieldValues: {},
  id: contact.id,
  contact,
});

export const AddLearnerToEventBooking = observer(() => {
  const [apiError, setApiError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveStates>(null);
  const [bookerIntention] = useBookerIntention();
  const { viewer } = useViewer();
  const isSelfRegistering = bookerIntention === BookerIntention.Self;
  const { t } = useTranslation();
  const { bookableId } = useParams<{ bookableId: string }>();
  const history = useHistory();
  const { refetch } = useAlreadyRegisteredEventIds();
  const { dateFormat } = useDateFormatter({ showTime: true });

  const [registerLearnersOntoPrivateBooking] = useWebLinkMutation<
    Pick<Mutation, "booking">
  >(REGISTER_LEARNERS_ONTO_PRIVATE_BOOKING_MUTATION);

  const client = useWebLinkClient();
  const contacts = async (inputValue: string) => {
    if (isSelfRegistering) return [];
    // TODO filter out Contacts already registered on event (pass down some `excludeContactIds` prop)
    return client
      .query({
        query: GET_MANAGED_CONTACTS_FOR_EVENT,
        variables: {
          eventId: bookableId,
          ...getContactVariables(inputValue),
        },
      })
      .then(result => {
        return [
          UNNAMED_LEARNER_CONTACT,
          ...extractNodes(result.data.managedContacts.edges),
        ];
      });
  };

  const values = useTypedFormValues<LearnersFormValues>({
    learnerNumber: 1,
    learners: [getInitialContact(UNNAMED_LEARNER_CONTACT)],
    learnerLength: 1,
  });
  useEffect(() => {
    if (isSelfRegistering && viewer) {
      values.set("learners", [
        getInitialContact({
          id: viewer.id!,
          firstName: viewer.firstName!,
          lastName: viewer.lastName!,
          emailAddress: viewer.emailAddress,
        }),
      ]);
    }
  }, [values, isSelfRegistering, viewer]);

  const [query, queryName] =
    bookerIntention === BookerIntention.Coordinating
      ? [PRIVATE_TRAINING_COORDINATOR_QUERY, "privateTrainingForCoordinator"]
      : [PRIVATE_TRAINING_SELF_QUERY, "privateTraining"];
  const { loading, error, data } = useWebLinkQuery(query, {
    variables: {
      filters: [
        {
          field: EventField.Id,
          operation: FilterOperation.Eq,
          value: bookableId,
        },
      ],
    },
  });
  const bookable = data?.[queryName].edges?.[0].node || DEFAULT_EVENT;
  const maxLearners = bookable.maxPlaces ? bookable.remainingPlaces : null;
  const { data: pointOfSaleFieldsData, loading: pointOfSaleFieldsLoading } =
    useWebLinkQuery<Query>(POINT_OF_SALE_LEARNER_FIELD_QUERY, {
      variables: {
        entityType: PointOfSaleFieldEntityType.CartLineItemEventLearner,
        filters: [
          {
            value: bookable?.course?.id,
            field: PointOfSaleField.IsValidForCourseTemplate,
            operation: FilterOperation.Eq,
          },
        ],
      },
    });
  const pointOfSaleFields =
    pointOfSaleFieldsData?.pointOfSaleLearnerFields ?? [];

  if (pointOfSaleFields.length > 0) {
    values.learners.forEach(learner => {
      pointOfSaleFields.forEach(field => {
        ensureObservableValue(learner.posFieldValues, field.key);
      });
    });
  }

  const handleSubmit = async () => {
    setSaveState("saving");
    const learners = {
      existingLearners: values.learners
        .filter(({ id }) => id !== "unnamed")
        .map(({ contact: { id }, posFieldValues }) => ({
          contactId: id,
          attributes: mapPointOfSaleFieldValues(
            pointOfSaleFields,
            posFieldValues,
          ),
        })),
      unnamedLearners: values.learners
        .filter(({ id }) => id === "unnamed")
        // sending an empty object neutralises system setting 'lead-useBookingContactAsStudent'
        // (without the empty object, an active setting would cause the learner to be named as the Opportunity Contact)
        .map(() => ({})),
    };

    const response = await registerLearnersOntoPrivateBooking({
      variables: {
        input: {
          bookingId: bookableId,
          quantity: values.learnerLength,
          learners,
        },
      },
    });

    const errors =
      response.errors ??
      response.data?.booking?.registerLearnersOntoPrivateBooking.errors ??
      [];
    setApiError(
      errors
        .map(error =>
          error && "message" in error ? error.message : String(error),
        )
        .join("; ") ?? null,
    );
    if (errors.length) {
      return;
    }
    refetch();
    if (isSelfRegistering) {
      const newLearnerId =
        response?.data?.booking?.registerLearnersOntoPrivateBooking
          ?.learners?.[0]?.id;
      history.push(`/my-courses/course/${newLearnerId}/registrationSuccess`);
      return;
    }
    history.push(`/learner-management/bookings/${bookable.id}/event`);
  };

  if (loading || pointOfSaleFieldsLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <Alert type="error" message={t("thereWasAProblemFetchingThisBooking")} />
    );
  }

  return (
    <Fragment>
      <Header
        title={bookable.name}
        usage="portal"
        back={{
          label: t("booking"),
          onClick: () =>
            history.push(`/learner-management/bookings/${bookableId}/event`),
        }}
      />
      <div className="result-page">
        <Container>
          {apiError && (
            <Alert
              type="error"
              message={apiError}
              glyph="removeSign"
              overPage={false}
            />
          )}
          <Row>
            <Col md={4}>
              <ItemInformation
                data={{
                  location: bookable.location ? bookable.location : undefined,
                  dateAndTime: bookable.start
                    ? formatDateRange(
                        bookable.start,
                        bookable.end || "",
                        dateFormat,
                      )
                    : undefined,
                  instructors: bookable.instructors,
                }}
                loading={loading}
                name={t("course")}
                image={bookable.imageUrl}
              />
            </Col>
            <Col md={8}>
              <Card>
                <div className="detail-form">
                  <Form
                    type="raw"
                    values={values}
                    onSubmit={handleSubmit}
                    disabled={saveState === "saving"}
                  >
                    <FormConsumer>
                      {({ values, errors }) => (
                        <Observer>
                          {() => (
                            <Fragment>
                              <SectionHeader title={t("addLearner")}>
                                <Fragment>
                                  <Submit
                                    disabled={loading || hasErrors(errors)}
                                  />
                                </Fragment>
                              </SectionHeader>
                              <MultipleLearnerSelector
                                contacts={contacts}
                                values={values}
                                maxLearners={maxLearners}
                                pointOfSaleFields={pointOfSaleFields}
                                disabled={isSelfRegistering}
                              />
                            </Fragment>
                          )}
                        </Observer>
                      )}
                    </FormConsumer>
                  </Form>
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
});
